import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../lib/api.js'
import { gotoProjekt } from '../lib/moduleLink.js'

export default function SearchSpotlight({ onNavigate }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState({ emails: [], contacts: [], projects: [] })
  const [loading, setLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef(null)
  const debounceRef = useRef(null)

  // Cmd+/ to toggle
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault()
        setOpen(prev => !prev)
      }
      if (e.key === 'Escape' && open) {
        setOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  useEffect(() => {
    if (open) {
      setQuery('')
      setResults({ emails: [], contacts: [], projects: [] })
      setSelectedIndex(0)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  const search = useCallback((q) => {
    if (!q || q.length < 2) {
      setResults({ emails: [], contacts: [], projects: [] })
      setLoading(false)
      return
    }
    setLoading(true)
    Promise.all([
      api.emails({ search: q, limit: 5 }).catch(() => ({ emails: [] })),
      api.contacts({ search: q, limit: 5 }).catch(() => ({ contacts: [] })),
      api.projects().catch(() => []),
    ]).then(([emailData, contactData, projectData]) => {
      const projects = (Array.isArray(projectData) ? projectData : [])
        .filter(p => p.name?.toLowerCase().includes(q.toLowerCase()))
        .slice(0, 5)
      setResults({
        emails: emailData.emails || [],
        contacts: contactData.contacts || [],
        projects,
      })
      setSelectedIndex(0)
      setLoading(false)
    })
  }, [])

  function onInput(e) {
    const q = e.target.value
    setQuery(q)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => search(q), 250)
  }

  const allItems = [
    ...results.projects.map(p => ({ type: 'project', label: p.name, sub: p.status, id: p.id })),
    ...results.contacts.map(c => ({ type: 'contact', label: c.name || c.email, sub: c.company || c.email, id: c.id })),
    ...results.emails.map(e => ({ type: 'email', label: e.subject || '(Kein Betreff)', sub: e.from_name || e.from_address, id: e.id })),
  ]

  function handleSelect(item) {
    setOpen(false)
    // Projekt-Treffer direkt auf die Projektseite (projektzentrierte Navigation)
    if (item.type === 'project') { gotoProjekt(item.id) }
    else if (item.type === 'contact') onNavigate('contacts')
    else if (item.type === 'email') onNavigate('emails')
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(i => Math.min(i + 1, allItems.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && allItems[selectedIndex]) {
      e.preventDefault()
      handleSelect(allItems[selectedIndex])
    }
  }

  if (!open) return null

  const typeIcon = {
    project: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
    contact: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
    email: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
  }
  const typeLabel = { project: 'Projekte', contact: 'Kontakte', email: 'Emails' }

  return (
    <div
      onClick={() => setOpen(false)}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'var(--surface-overlay)', backdropFilter: 'blur(3px)',
        display: 'flex', justifyContent: 'center', paddingTop: '18vh',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 520, maxHeight: 420,
          background: 'var(--color-bg, #fff)',
          borderRadius: 12,
          boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
          overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          alignSelf: 'flex-start',
        }}
      >
        {/* Search input */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--color-border-light)', gap: 10 }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2" style={{ width: 18, height: 18, flexShrink: 0 }}>
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={onInput}
            onKeyDown={onKeyDown}
            placeholder="Suche nach Projekten, Kontakten, Emails..."
            style={{
              flex: 1, border: 'none', outline: 'none', fontSize: 15,
              background: 'transparent', color: 'var(--color-text)',
            }}
          />
          <kbd style={{ fontSize: 11, color: 'var(--color-text-muted)', background: 'var(--color-bg-muted)', padding: '2px 6px', borderRadius: 4 }}>ESC</kbd>
        </div>

        {/* Results */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading && (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 13 }}>Suche...</div>
          )}
          {!loading && query.length >= 2 && allItems.length === 0 && (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 13 }}>Keine Ergebnisse</div>
          )}
          {!loading && allItems.length > 0 && (() => {
            let lastType = null
            let globalIndex = -1
            return allItems.map((item, i) => {
              globalIndex++
              const showHeader = item.type !== lastType
              lastType = item.type
              const idx = globalIndex
              return (
                <div key={`${item.type}-${item.id}`}>
                  {showHeader && (
                    <div style={{ padding: '8px 16px 4px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {typeLabel[item.type]}
                    </div>
                  )}
                  <div
                    onClick={() => handleSelect(item)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 16px', cursor: 'pointer',
                      background: idx === selectedIndex ? 'var(--color-primary)' : 'transparent',
                      color: idx === selectedIndex ? '#fff' : 'var(--color-text)',
                      transition: 'background 80ms',
                    }}
                    onMouseEnter={() => setSelectedIndex(idx)}
                  >
                    <span style={{ opacity: 0.7, display: 'flex', alignItems: 'center' }}>{typeIcon[item.type]}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 450, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</div>
                      {item.sub && <div style={{ fontSize: 11, opacity: 0.6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.sub}</div>}
                    </div>
                  </div>
                </div>
              )
            })
          })()}
          {!query && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 13 }}>
              Tippe, um Projekte, Kontakte und Emails zu durchsuchen
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div style={{ padding: '8px 16px', borderTop: '1px solid var(--color-border-light)', display: 'flex', gap: 12, fontSize: 11, color: 'var(--color-text-muted)' }}>
          <span><kbd style={{ background: 'var(--color-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>↑↓</kbd> Navigieren</span>
          <span><kbd style={{ background: 'var(--color-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>↵</kbd> Öffnen</span>
          <span><kbd style={{ background: 'var(--color-bg-muted)', padding: '1px 4px', borderRadius: 3 }}>esc</kbd> Schliessen</span>
        </div>
      </div>
    </div>
  )
}
